import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { User, Star, Lock, Unlock, Flame, BookOpen, MessageCircle, Moon, Sparkles, Crown, FileText, Shield, CreditCard, Loader2 } from 'lucide-react';
import api from '../lib/api';
import Navigation from '../components/Navigation';

const LEVEL_SIGILS = ['', '\u2609', '\u263F', '\u2640', '\u2643', '\u2644', '\u2645', '\u2646'];

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payTab, setPayTab] = useState({});
  const [loadingPlan, setLoadingPlan] = useState(null);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const { data } = await api.get('/profile/stats');
      setStats(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070A1A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070A1A]" data-testid="profile-page">
      <Navigation />
      <div className="px-4 md:px-12 lg:px-24 py-8 pb-24 md:pb-8 max-w-4xl mx-auto">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 mb-8 text-center"
          data-testid="profile-header"
        >
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 border-2 border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-['Playfair_Display'] text-[#D4AF37]">
              {user?.username?.[0]?.toUpperCase() || 'S'}
            </span>
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0]" data-testid="profile-username">{user?.username}</h1>
          <p className="text-sm text-[#BDB2C4] mt-1">Soul Seeker since {stats?.created_at ? new Date(stats.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '...'}</p>
          {stats?.sun_sign && (
            <p className="text-sm text-[#D4AF37] mt-2">You carry the energy of {stats.sun_sign}</p>
          )}
        </motion.div>

        {/* Insight Level */}
        {stats?.level && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6 mb-8"
            data-testid="insight-level"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center animate-glow">
                <span className="text-2xl text-[#D4AF37]">{LEVEL_SIGILS[stats.level.level] || '\u2609'}</span>
              </div>
              <div>
                <p className="text-xs text-[#D4AF37] tracking-widest uppercase">Level {stats.level.level}</p>
                <h2 className="font-['Playfair_Display'] text-xl text-[#FFFFF0]">{stats.level.name}</h2>
                <p className="text-sm text-[#BDB2C4] italic">{stats.level.description}</p>
              </div>
            </div>
            {stats.level.level < 7 && stats.next_level && (
              <div data-testid="level-progress">
                <div className="flex justify-between text-xs text-[#BDB2C4] mb-2">
                  <span>Level {stats.level.level}: {stats.level.name}</span>
                  <span>Level {stats.next_level.level}: {stats.next_level.name}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${stats.level_progress || 0}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full rounded-full bg-gradient-to-r from-[#D4AF37]/60 to-[#D4AF37]"
                  />
                </div>
                <p className="text-[10px] text-[#BDB2C4] mt-2 text-center">
                  {stats.readings_to_next_level} more reading{stats.readings_to_next_level !== 1 ? 's' : ''} to reach {stats.next_level.name}
                </p>
              </div>
            )}
            {stats.level.level >= 7 && (
              <p className="text-xs text-[#D4AF37] mt-2 font-['Playfair_Display'] italic text-center">You have reached the highest level of insight.</p>
            )}
          </motion.div>
        )}

        {/* Soul Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6 mb-8"
          data-testid="soul-stats"
        >
          <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0] mb-4">Soul Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame size={14} className="text-[#C87886]" />
                <p className="text-xl font-['Playfair_Display'] text-[#D4AF37]">{stats?.streak || 0}</p>
              </div>
              <p className="text-xs text-[#BDB2C4]">days in the Room</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-['Playfair_Display'] text-[#D4AF37]">{stats?.readings_count || 0}</p>
              <p className="text-xs text-[#BDB2C4]">readings completed</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-['Playfair_Display'] text-[#D4AF37]">{stats?.reflections_count || 0}</p>
              <p className="text-xs text-[#BDB2C4]">reflections written</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-['Playfair_Display'] text-[#D4AF37]">{stats?.chat_count || 0}</p>
              <p className="text-xs text-[#BDB2C4]">Guide conversations</p>
            </div>
          </div>

          {stats?.suit_message && (
            <p className="text-sm text-[#BDB2C4] mt-4 italic text-center" data-testid="suit-insight">{stats.suit_message}</p>
          )}
          {stats?.top_intention && (
            <p className="text-sm text-[#BDB2C4] mt-2 italic text-center">You most often seek {stats.top_intention}.</p>
          )}
        </motion.div>

        {/* Most Visited Cards */}
        {stats?.top_cards?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6 mb-8"
            data-testid="familiar-cards"
          >
            <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0] mb-4">The Cards That Know You</h3>
            <div className="flex flex-wrap gap-4">
              {stats.top_cards.map((card, i) => (
                <div key={i} className="glass-card p-4 flex items-center gap-3 border-[#D4AF37]/10">
                  <Star size={16} className="text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-[#FFFFF0]">{card.name}</p>
                    <p className="text-xs text-[#BDB2C4]">appeared {card.count} times</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Unlockable Spreads */}
        {stats?.spreads && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6 mb-8"
            data-testid="unlockable-spreads"
          >
            <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0] mb-4">Spreads</h3>
            <div className="space-y-3">
              {stats.spreads.map(spread => (
                <div key={spread.id} className={`flex items-center gap-4 p-3 rounded-xl transition-all ${spread.locked ? 'opacity-50' : 'glass-card'}`}>
                  {spread.locked ? <Lock size={16} className="text-[#BDB2C4]" /> : <Unlock size={16} className="text-[#D4AF37]" />}
                  <div>
                    <p className={`text-sm ${spread.locked ? 'text-[#BDB2C4]' : 'text-[#FFFFF0]'}`}>{spread.name}</p>
                    <p className="text-xs text-[#BDB2C4]">{spread.description}</p>
                    {spread.locked && <p className="text-xs text-[#C87886] mt-1">Unlocks at Level {spread.unlock_level}</p>}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-card p-6 mb-8"
          data-testid="subscription-section"
        >
          <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0] mb-4">Your Subscription</h3>
          <div className="mb-5 p-4 rounded-xl bg-white/5 border border-white/10">
            <p className="text-sm text-[#FFFFF0] capitalize">{stats?.subscription_status === 'free' ? 'Free Tier' : stats?.subscription_status?.replace('_', ' ')}</p>
            {stats?.subscription_status === 'free' && (
              <p className="text-xs text-[#C87886] mt-1">{stats?.readings_count || 0} of 2 free readings used</p>
            )}
            {stats?.subscription_expires_at && stats?.subscription_status !== 'free' && (
              <p className="text-xs text-[#BDB2C4] mt-1">Expires: {new Date(stats.subscription_expires_at).toLocaleDateString()}</p>
            )}
          </div>
          <p className="text-xs text-[#BDB2C4] mb-4">Upgrade to unlock unlimited readings and deeper guidance.</p>
          <div className="space-y-4">
            {[
              { id: 'day_pass', name: 'One Day Pass', price_usd: '$1', price_inr: '\u20B969', desc: "For Today's Questions - Unlimited", icon: Moon },
              { id: 'monthly', name: 'Monthly Pass', price_usd: '$3.99', price_inr: '\u20B9299', desc: 'Unlimited readings, all month', icon: Sparkles, recommended: true },
              { id: 'quarterly', name: '3 Months Pass', price_usd: '$7.99', price_inr: '\u20B9699', desc: 'Three months of Deeper Clarity', icon: Star },
              { id: 'yearly', name: '1 Year Pass', price_usd: '$32.99', price_inr: '\u20B92,999', desc: 'Your cards all year long', icon: Crown },
            ].map(plan => {
              const Icon = plan.icon;
              const currentTab = payTab[plan.id] || 'card';
              const isLoading = loadingPlan === plan.id;
              return (
                <div key={plan.id}
                  data-testid={`profile-plan-${plan.id}`}
                  className={`glass-card p-6 transition-all duration-300 hover:-translate-y-1 ${plan.recommended ? 'border-[#D4AF37]/40 shadow-[0_0_30px_rgba(212,175,55,0.1)]' : ''}`}>
                  {plan.recommended && (
                    <span className="text-[10px] text-[#D4AF37] tracking-widest uppercase bg-[#D4AF37]/10 px-3 py-1 rounded-full mb-3 inline-block">Recommended</span>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <Icon size={20} className="text-[#D4AF37]" strokeWidth={1.5} />
                    <h4 className="font-['Playfair_Display'] text-lg text-[#FFFFF0]">{plan.name}</h4>
                  </div>
                  <p className="text-sm text-[#BDB2C4] mb-4">{plan.desc}</p>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-xl font-['Playfair_Display'] text-[#D4AF37]">{plan.price_usd}</span>
                    <span className="text-xs text-[#BDB2C4]">/ {plan.price_inr}</span>
                  </div>
                  <div className="flex rounded-lg overflow-hidden border border-white/10 mb-3">
                    <button onClick={() => setPayTab(prev => ({ ...prev, [plan.id]: 'card' }))}
                      className={`flex-1 py-2 text-xs font-medium transition-all ${currentTab === 'card' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#BDB2C4] hover:text-[#FFFFF0]'}`}>
                      Pay in USD
                    </button>
                    <button onClick={() => setPayTab(prev => ({ ...prev, [plan.id]: 'upi' }))}
                      className={`flex-1 py-2 text-xs font-medium transition-all ${currentTab === 'upi' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#BDB2C4] hover:text-[#FFFFF0]'}`}>
                      Pay in INR
                    </button>
                  </div>
                  {currentTab === 'card' ? (
                    <button onClick={async () => {
                      setLoadingPlan(plan.id);
                      try {
                        const { data } = await api.post('/payments/create-checkout', { plan_id: plan.id, origin_url: window.location.origin });
                        if (data.url) window.location.href = data.url;
                      } catch (e) { alert(e.response?.data?.detail || 'Payment failed.'); }
                      finally { setLoadingPlan(null); }
                    }} disabled={isLoading}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                      {isLoading ? <><Loader2 size={14} className="animate-spin" /> Processing...</> : <><CreditCard size={14} /> Pay {plan.price_usd}</>}
                    </button>
                  ) : (
                    <button onClick={async () => {
                      setLoadingPlan(plan.id);
                      try {
                        if (!document.getElementById('razorpay-script')) {
                          const s = document.createElement('script'); s.id = 'razorpay-script'; s.src = 'https://checkout.razorpay.com/v1/checkout.js';
                          document.body.appendChild(s);
                          await new Promise(r => { s.onload = () => r(); s.onerror = () => r(); });
                        }
                        const { data } = await api.post('/payments/razorpay/create-order', { plan_id: plan.id });
                        const rzp = new window.Razorpay({
                          key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
                          name: 'Tarot Room', description: plan.name,
                          prefill: { name: user?.username || '', email: user?.email || '' },
                          theme: { color: '#D4AF37' },
                          handler: async (r) => {
                            try {
                              const v = await api.post('/payments/razorpay/verify', {
                                razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id,
                                razorpay_signature: r.razorpay_signature, plan_id: plan.id
                              });
                              if (v.data.verified && refreshUser) { await refreshUser(); loadStats(); }
                            } catch { alert('Verification failed.'); }
                          },
                          modal: { ondismiss: () => setLoadingPlan(null) }
                        });
                        rzp.open();
                      } catch (e) { alert(e.response?.data?.detail || 'Payment failed.'); }
                      finally { setLoadingPlan(null); }
                    }} disabled={isLoading}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50">
                      {isLoading ? <><Loader2 size={14} className="animate-spin" /> Processing...</> : <>Pay {plan.price_inr}</>}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Legal Links */}
        <div className="flex items-center justify-center gap-6 mt-4 mb-4" data-testid="legal-links">
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#BDB2C4] hover:text-[#D4AF37] transition-colors" data-testid="profile-terms-link">
            <FileText size={14} />
            Terms & Conditions
          </a>
          <span className="text-[#BDB2C4]/30">|</span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#BDB2C4] hover:text-[#D4AF37] transition-colors" data-testid="profile-privacy-link">
            <Shield size={14} />
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}
